import { Project, Smqs } from 'types/types';

// Metadata and server-computed values. None of these is data the user entered, so a
// difference in one must never revert a project to Draft. `updated_at` / `updated_by`
// matter most: they change on every single save, so comparing them would revert every
// submitted project the moment it was opened.
const IGNORED_FIELDS: readonly string[] = [
  'id',
  'is_submitted',
  'submitted_at',
  'submitted_by',
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
  'is_my_project',
  'total_gwp_kg_exclude_d',
  'total_intensity_exclude_d',
  'biogenic_carbon_content',
  'gross_area_sqm',
  'ECOM_version',
];

const emptyToNull = (value: unknown): unknown => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  return value;
};

// Numbers arrive in three shapes for the same value: the number 5 from a stored int, the
// string "5" from a text input, and the string "5.00" from a DRF DecimalField. All three
// mean the same thing to the database. Non-numeric values pass through as strings.
const numericOrSelf = (value: unknown): unknown => {
  const normalized = emptyToNull(value);
  if (normalized === null || typeof normalized === 'boolean') return normalized;

  const asNumber = Number(normalized);

  return Number.isFinite(asNumber) ? asNumber : String(normalized);
};

// Two values are "the same" if the backend would store the same thing.
//
// Where that is ambiguous this deliberately errs towards "unchanged". A false positive
// un-submits a project nobody edited - the defect this branch exists to fix - while a
// false negative merely leaves a submitted project submitted, which is today's
// behaviour. One known consequence: a zipcode edited from "02201" to "2201" reads as
// unchanged, because both parse to the same number. That is the trade accepted here.
const isSameValue = (stored: unknown, outgoing: unknown): boolean => {
  const a = numericOrSelf(stored);
  const b = numericOrSelf(outgoing);

  if (a === null || b === null) return a === b;

  if (typeof a === 'object' || typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  return a === b;
};

// Only the SMQ fields the wizard actually edits. Server rows also carry `project` and a
// fully nested `ecom_ref` (serializers.py:204-208) that the form never holds, so
// comparing whole rows would report a change on every step 3-5 save and un-submit a
// project nobody touched. `material_type.unit` is derived from the name, so it is left
// out too.
const canonicalSmqRow = (row: Partial<Smqs> | undefined): string => JSON.stringify([
  row?.field?.overall_structure ?? null,
  row?.field?.structure ?? null,
  row?.material_type?.category ?? null,
  row?.material_type?.name ?? null,
  row?.is_lateral ?? null,
  numericOrSelf(row?.quantity),
  numericOrSelf(row?.a1_a3_carbon_factor_override),
]);

// Rows are a set of quantities, not an ordered list: ProjectSerializer.update() deletes
// and recreates all of them on every save, so the order coming back is not the order
// that went out. Sorting makes a reorder alone read as unchanged.
const canonicalSmqs = (smqs: unknown): string => {
  if (!Array.isArray(smqs)) return '[]';

  return JSON.stringify(smqs.map(canonicalSmqRow).sort());
};

export interface HasChangedProps {
  projectDetails: Project;
  patch: Record<string, unknown>;
}

// True when the outgoing payload would store a different value for any field the user
// owns. Only fields the payload actually carries are compared - a step save carries its
// own step's fields, so a step can only revert a project over data it can see.
//
// `projectDetails` mirrors the last saved server response (updateContextWithResponse),
// which is what makes this a comparison against stored state rather than against
// whatever a page last typed.
export const hasProjectDataChanged = ({ projectDetails, patch }: HasChangedProps): boolean => {
  const stored = projectDetails as unknown as Record<string, unknown>;

  return Object.keys(patch).some(field => {
    if (IGNORED_FIELDS.includes(field)) return false;

    if (field === 'smqs') {
      return canonicalSmqs(stored.smqs) !== canonicalSmqs(patch.smqs);
    }

    return !isSameValue(stored[field], patch[field]);
  });
};
